import React from "react";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import { Button } from "../Button";
import {
  BRAND_HERO_BLUE,
  SUCCESS_20,
  WARNING_20,
  ERROR_20,
  ERROR_90,
  SUCCESS_90,
  WARNING_90,
  WHITE,
  ERROR_30,
} from "../../design-tokens";
import type { ModalType } from "./types";

interface ModalHeaderProps {
  title?: string;
  subtitle?: string;
  hideClose?: boolean;
  type?: ModalType;
  className?: string;
  hideIcon?: boolean;
  onClose: () => void;
  closeButtonTestId?: string;
}

const modalIconType = {
  success: SvgNames.SuccessOutline,
  error: SvgNames.ErrorOutline,
  warning: SvgNames.WarningOutline,
  info: SvgNames.InfoOutline,
  destructive: SvgNames.WarningOutline,
};

const modalTextColor = {
  success: SUCCESS_20,
  error: ERROR_20,
  warning: WARNING_20,
  info: BRAND_HERO_BLUE,
  destructive: WHITE,
};

const modalBackgroundColor = {
  success: SUCCESS_90,
  error: ERROR_90,
  warning: WARNING_90,
  info: WHITE,
  destructive: ERROR_30,
};

const ModalHeader = ({
  title,
  type = "info",
  subtitle,
  hideClose,
  className,
  hideIcon,
  onClose,
  closeButtonTestId,
}: ModalHeaderProps) => {
  const isContentOnly = !title;

  if (isContentOnly) {
    return (
      <HeaderContainer
        type={type}
        data-testid="modal-header"
        className={className ?? ""}
        contentOnly={isContentOnly}
      >
        {!hideClose && (
          <CloseBtnContainer>
            <CustomButton
              onClick={onClose}
              size="sm"
              color="tertiary"
              data-testid={closeButtonTestId || "modal-close"}
              iconMiddle={<SvgIcon name={SvgNames.CloseFilled} />}
            />
          </CloseBtnContainer>
        )}
      </HeaderContainer>
    );
  }

  return (
    <HeaderContainer
      type={type}
      data-testid="modal-header"
      className={className}
      contentOnly={isContentOnly}
    >
      <div>
        {!hideIcon && title && (
          <IconContainer>
            <SvgIcon
              data-testid="modal-header-icon"
              color={modalTextColor[type]}
              width={80}
              height={80}
              name={modalIconType[type]}
            />
          </IconContainer>
        )}
        {title && (
          <TitleTypography type={type} variant="h1">
            {title}
          </TitleTypography>
        )}
        {subtitle && (
          <SubtitleTypography type={type} role="secondary">
            {subtitle}
          </SubtitleTypography>
        )}
      </div>
      {!hideClose && type !== "destructive" && (
        <CloseBtnContainer>
          <CustomButton
            onClick={onClose}
            size="sm"
            color="tertiary"
            iconMiddle={
              <SvgIcon name={SvgNames.CloseFilled} color={BRAND_HERO_BLUE} />
            }
            data-testid={closeButtonTestId || "modal-close"}
          />
        </CloseBtnContainer>
      )}
      {!hideClose && type === "destructive" && (
        <CloseBtnContainer>
          <CustomButton
            onClick={onClose}
            size="sm"
            color="tertiary"
            data-testid={closeButtonTestId || "modal-close-destructive"}
            iconMiddle={<SvgIcon name={SvgNames.CloseFilled} color={WHITE} />}
          />
        </CloseBtnContainer>
      )}
    </HeaderContainer>
  );
};

const CustomButton = styled(Button)`
  padding: 0px;
  & > svg {
    width: 40px;
    height: 40px;
  }
`;

const HeaderContainer = styled.div<{
  type: ModalType;
  contentOnly?: boolean;
}>`
  display: flex;
  padding: 40px 40px 32px 40px;
  padding-bottom: ${(props) => (props.contentOnly ? 0 : 32)}px;
  border-radius: 7px 7px 0px 0px;
  background-color: ${(props) => modalBackgroundColor[props.type]};
`;

const CloseBtnContainer = styled.div`
  margin-left: auto;
`;

const TitleTypography = styled(Typography)<{ type: ModalType }>`
  color: ${(props) => {
    if (props.role === "secondary" && props.type === "info") {
      return undefined;
    }
    return modalTextColor[props.type];
  }};
`;

const SubtitleTypography = styled(TitleTypography)`
  margin-top: 16px;
`;

const IconContainer = styled.div`
  margin-bottom: 24px;
`;

export { ModalHeader };
